import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

interface CookieConsentPreferences {
  essential: true;
  analytics: boolean;
  updatedAt: string;
  version: number;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type AnalyticsGlobal = typeof globalThis & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly storageKey = 'fit_cookie_consent_v1';
  private readonly consentVersion = 1;
  private readonly consentSubject = new BehaviorSubject<CookieConsentPreferences | null>(this.readStoredConsent());

  private analyticsInitialized = false;
  private analyticsScriptLoaded = false;

  private get analyticsGlobal(): AnalyticsGlobal {
    return globalThis;
  }

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    if (this.isAnalyticsAllowed()) {
      this.enableAnalytics();
    }
  }

  hasConsentChoice(): boolean {
    return this.consentSubject.value !== null;
  }

  isAnalyticsAllowed(): boolean {
    return this.consentSubject.value?.analytics === true;
  }

  acceptAll(): void {
    this.saveConsent(true);
  }

  rejectNonEssential(): void {
    this.saveConsent(false);
  }

  saveAnalyticsPreference(analytics: boolean): void {
    this.saveConsent(analytics);
  }

  private saveConsent(analytics: boolean): void {
    const consent: CookieConsentPreferences = {
      essential: true,
      analytics,
      updatedAt: new Date().toISOString(),
      version: this.consentVersion
    };

    localStorage.setItem(this.storageKey, JSON.stringify(consent));
    this.consentSubject.next(consent);

    if (analytics) {
      this.enableAnalytics();
      return;
    }

    this.disableAnalytics();
  }

  private enableAnalytics(): void {
    if (!this.canLoadAnalytics()) {
      return;
    }

    if (!this.analyticsInitialized) {
      this.bootstrapGtag();
      this.injectAnalyticsScript(environment.gaMeasurementId);
      this.analyticsInitialized = true;
    }

    if (this.analyticsScriptLoaded) {
      this.applyGrantedAnalyticsConsent();
    }
  }

  private canLoadAnalytics(): boolean {
    return Boolean(environment.gaMeasurementId);
  }

  private disableAnalytics(): void {
    if (typeof this.analyticsGlobal.gtag === 'function') {
      this.analyticsGlobal.gtag('consent', 'update', { analytics_storage: 'denied' });
    }

    this.clearAnalyticsCookies();
  }

  private applyGrantedAnalyticsConsent(): void {
    if (typeof this.analyticsGlobal.gtag !== 'function' || !environment.gaMeasurementId) {
      return;
    }

    this.analyticsGlobal.gtag('consent', 'update', { analytics_storage: 'granted' });
    this.analyticsGlobal.gtag('config', environment.gaMeasurementId, {
      anonymize_ip: true,
      debug_mode: !environment.production,
      send_page_view: true
    });

    this.analyticsGlobal.gtag('event', 'page_view', {
      page_path: this.document.location.pathname,
      page_title: this.document.title,
      page_location: this.document.location.href
    });
  }

  private bootstrapGtag(): void {
    this.analyticsGlobal.dataLayer = this.analyticsGlobal.dataLayer || [];

    if (typeof this.analyticsGlobal.gtag !== 'function') {
      this.analyticsGlobal.gtag = function gtag() {
        (globalThis as AnalyticsGlobal).dataLayer?.push(arguments);
      };
    }

    this.analyticsGlobal.gtag('js', new Date());
    this.analyticsGlobal.gtag('consent', 'default', { analytics_storage: 'denied' });
  }

  private injectAnalyticsScript(measurementId: string): void {
    const existingScript = this.document.getElementById('ga-gtag-script');
    if (existingScript) {
      this.analyticsScriptLoaded = true;
      this.applyGrantedAnalyticsConsent();
      return;
    }

    const script = this.document.createElement('script');
    script.id = 'ga-gtag-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => {
      this.analyticsScriptLoaded = true;
      this.applyGrantedAnalyticsConsent();
    };
    this.document.head.appendChild(script);
  }

  private clearAnalyticsCookies(): void {
    const names = this.document.cookie
      .split(';')
      .map((cookie) => cookie.trim().split('=')[0])
      .filter((name) => name.startsWith('_ga') || name === '_gid' || name.startsWith('_gat'));

    if (names.length === 0) {
      return;
    }

    const domains = this.getCandidateDomains(this.document.location.hostname);

    names.forEach((name) => {
      this.expireCookie(name);
      domains.forEach((domain) => this.expireCookie(name, domain));
    });
  }

  private expireCookie(name: string, domain?: string): void {
    const domainPart = domain ? `;domain=${domain}` : '';
    this.document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainPart}`;
  }

  private getCandidateDomains(hostname: string): string[] {
    const hostParts = hostname.split('.').filter(Boolean);
    const domains: string[] = [];

    for (let index = 0; index <= hostParts.length - 2; index += 1) {
      domains.push(`.${hostParts.slice(index).join('.')}`);
    }

    return domains;
  }

  private readStoredConsent(): CookieConsentPreferences | null {
    const rawValue = localStorage.getItem(this.storageKey);
    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<CookieConsentPreferences>;
      if (typeof parsed.analytics !== 'boolean') {
        return null;
      }

      if (parsed.version !== this.consentVersion) {
        localStorage.removeItem(this.storageKey);
        return null;
      }

      return {
        essential: true,
        analytics: parsed.analytics,
        updatedAt: parsed.updatedAt ?? new Date().toISOString(),
        version: this.consentVersion
      };
    } catch {
      localStorage.removeItem(this.storageKey);
      return null;
    }
  }
}
